/**
 * InsufficientRoleError — typed error mapped from leadbilling's `AuthRole`
 * middleware response (HTTP 403, `errors.code === 'insufficient_role'`).
 *
 * Surfaced when a non-Owner/Admin caller tries to mutate one-time credits
 * (e.g. `POST /workspaces/:uuid/addons/one-time-credits`). Caught in
 * `ConfirmPurchaseModal` to render an informative toast while keeping the
 * modal open.
 *
 * Aligns with the repo's `tClassifiedError` pattern (see
 * `NoPaymentMethodError`): an `Error` carrying a stable `code` discriminator
 * that consumers detect via `instanceof` (preferred) or duck-type on
 * `error.code`.
 */
export class InsufficientRoleError extends Error {
  /** Discriminator matching the backend `errors.code` machine code. */
  readonly code = 'insufficient_role' as const;

  constructor(
    message = 'Caller role is not allowed to mutate one-time credits.',
  ) {
    super(message);
    this.name = 'InsufficientRoleError';
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, InsufficientRoleError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isInsufficientRoleError = (
  err: unknown,
): err is InsufficientRoleError =>
  err instanceof InsufficientRoleError ||
  (err instanceof Error &&
    'code' in err &&
    (err as { code?: unknown }).code === 'insufficient_role');
